import { supabase } from '@/integrations/supabase/client';
import type { ForjaNotesFolder, ForjaNote, NoteType, VideoPlatform } from '@/types/forjaNotes';
import { canUploadFile, extractImageUrls, extractFilePathFromUrl, deleteStorageFile } from '@/services/storageQuota';

// Optimized column selections
const FOLDER_COLUMNS = 'id, user_id, name, display_order, created_at, updated_at';
const NOTE_COLUMNS = 'id, user_id, folder_id, title, content, display_order, created_at, updated_at, note_type, video_url, video_platform, cached_transcription';
// Folders
export async function fetchFolders(): Promise<ForjaNotesFolder[]> {
  const { data, error } = await (supabase as any)
    .from('forja_notes_folders')
    .select(FOLDER_COLUMNS)
    .order('display_order', { ascending: true });

  if (error) throw error;
  return data as ForjaNotesFolder[];
}

export async function createFolder(name: string): Promise<ForjaNotesFolder> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Usuário não autenticado');

  const { data, error } = await (supabase as any)
    .from('forja_notes_folders')
    .insert({ user_id: user.id, name })
    .select()
    .single();

  if (error) throw error;
  return data as ForjaNotesFolder;
}

export async function updateFolder(id: string, updates: Partial<Pick<ForjaNotesFolder, 'name' | 'display_order'>>): Promise<ForjaNotesFolder> {
  const { data, error } = await (supabase as any)
    .from('forja_notes_folders')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as ForjaNotesFolder;
}

export async function deleteFolder(id: string): Promise<void> {
  const { error } = await (supabase as any)
    .from('forja_notes_folders')
    .delete()
    .eq('id', id);

  if (error) throw error;
}

// Notes
export async function fetchNotes(): Promise<ForjaNote[]> {
  const { data, error } = await (supabase as any)
    .from('forja_notes')
    .select(NOTE_COLUMNS)
    .order('display_order', { ascending: true });

  if (error) throw error;
  return data as ForjaNote[];
}

export async function fetchNoteById(noteId: string): Promise<ForjaNote | null> {
  const { data, error } = await (supabase as any)
    .from('forja_notes')
    .select(NOTE_COLUMNS)
    .eq('id', noteId)
    .maybeSingle();

  if (error) throw error;
  return data as ForjaNote | null;
}

export async function createNote(
  folderId: string | null, 
  title: string = 'Nova Nota',
  noteType: NoteType = 'regular',
  videoUrl?: string,
  videoPlatform?: VideoPlatform
): Promise<ForjaNote> {
  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Usuário não autenticado');

  const insertData: Record<string, unknown> = {
    user_id: user.id, 
    folder_id: folderId,
    title: title,
    content: '',
    note_type: noteType
  };

  // Add video fields only for video_study type
  if (noteType === 'video_study') {
    insertData.video_url = videoUrl || null;
    insertData.video_platform = videoPlatform || null;
  }

  const { data, error } = await (supabase as any)
    .from('forja_notes')
    .insert(insertData)
    .select()
    .single();

  if (error) throw error;
  return data as ForjaNote;
}

export async function updateNote(id: string, updates: Partial<Pick<ForjaNote, 'title' | 'content' | 'folder_id' | 'display_order' | 'video_url' | 'video_platform' | 'cached_transcription'>>): Promise<ForjaNote> {
  const { data, error } = await (supabase as any)
    .from('forja_notes')
    .update(updates)
    .eq('id', id)
    .select()
    .single();

  if (error) throw error;
  return data as ForjaNote;
}

export async function deleteNote(id: string): Promise<void> {
  // First, get the note content to extract image URLs
  const { data: note } = await (supabase as any)
    .from('forja_notes')
    .select('content')
    .eq('id', id)
    .single();

  // Delete the note
  const { error } = await (supabase as any)
    .from('forja_notes')
    .delete()
    .eq('id', id);

  if (error) throw error;

  // Clean up associated images
  if (note?.content) {
    const imageUrls = extractImageUrls(note.content);
    for (const url of imageUrls) {
      const filePath = extractFilePathFromUrl(url);
      if (filePath) {
        try {
          await deleteStorageFile(filePath);
        } catch (err) {
          console.error('Error deleting image:', filePath, err);
          // Continue deleting other images even if one fails
        }
      }
    }
  }
}

// Image upload - now with compression, original size limit is more relaxed
const MAX_IMAGE_SIZE = 10485760; // 10MB in bytes (will be compressed to ~100-200KB)

export class StorageQuotaExceededError extends Error {
  constructor(message: string = 'Limite de armazenamento atingido. Libere espaço ou faça upgrade.') {
    super(message);
    this.name = 'StorageQuotaExceededError';
  }
}

export async function uploadNoteImage(file: File): Promise<string> {
  // Validate file size (max 10MB before compression)
  if (file.size > MAX_IMAGE_SIZE) {
    throw new Error('A imagem deve ter no máximo 10MB');
  }

  const { data: { user } } = await supabase.auth.getUser();
  if (!user) throw new Error('Usuário não autenticado');

  // Import and compress image (will reduce to ~100-200KB)
  const { prepareImageForUpload } = await import('@/lib/imageUtils');
  const { blob, extension, mimeType } = await prepareImageForUpload(file, 'forjaNotes');

  // Check storage quota with compressed size
  const hasSpace = await canUploadFile(blob.size);
  if (!hasSpace) {
    throw new StorageQuotaExceededError();
  }

  const fileName = `${user.id}/${Date.now()}.${extension}`;

  const { error } = await supabase.storage
    .from('forja-notes-images')
    .upload(fileName, blob, {
      contentType: mimeType
    });

  if (error) throw error;

  const { data: { publicUrl } } = supabase.storage
    .from('forja-notes-images')
    .getPublicUrl(fileName);

  return publicUrl;
}
